// useCustomFontLoader — extrahiert aus app/src/components/editor/Viewer/
// useCustomFontLoader.ts (Plan A Mapping-Audit 2026-05-22).
//
// FontInfoDto-Typ wurde von app/-types/actions.ts in dieses Modul inline-
// kopiert um die app/-Coupling aufzulösen. Das Schema ist seit Phase 1
// stabil.

import type { CustomFonts, FontMapping, FontMappingEintrag } from "@scrolly/post-types";
import { injectReaderStandardFonts } from "@scrolly/renderer";
import { useEffect, useMemo } from "react";

export interface FontInfoDto {
  familyName: string;
  weight: number;
  italic: boolean;
  variable: boolean;
  filename: string;
  fontUrl: string;
}

/**
 * Polyfill document.fonts.entries() for Firefox.
 * @see https://sidneyliebrand.io/blog/fixing-font-face-set-entries-not-iterable-in-firefox
 */
function fontFaceSetIteratorToArray(target: FontFaceSet) {
  const iterable = target.entries();
  const results: [FontFace, FontFace][] = [];
  let iterator = iterable.next();
  while (iterator.done === false) {
    results.push(iterator.value);
    iterator = iterable.next();
  }
  return results;
}

/**
 * Welche Schriften muss der Leser laden?
 *
 * ALLE, die im Scrolly hinterlegt sind — nicht nur die beiden aus dem
 * fontMapping (Ueberschrift/Fliesstext).
 *
 * Vorher stand hier eine Bedingung, die genau das tat:
 *   if ([textFont?.family, headlineFont?.family].includes(fi.familyName))
 * Damals gab es auch nur diese beiden Stellen, an denen man eine Schrift
 * waehlen konnte. Inzwischen kommen WebGL-Textebenen und der CTA-Button dazu —
 * deren Schrift stand zwar korrekt in `fontInfos`, wurde aber nie geladen, und
 * der Leser sah die Ersatzschrift (Jans Fund 05.08.2026: „die wird beim CTA
 * nicht im Preview angezeigt").
 *
 * `fontInfos` bedeutet „diese Schriften braucht dieser Scrolly" — es wird beim
 * Auswaehlen geschrieben. Sie alle zu laden ist damit die richtige Auslegung.
 * Eintraege ohne Familie oder Adresse fliegen raus: Die koennte man ohnehin
 * nicht laden.
 *
 * Doppelte zaehlen je SCHNITT (Familie + Staerke + kursiv), nicht je Familie:
 * Eine Kundenschrift kann mit mehreren hochgeladenen Schnitten hinterlegt sein
 * (Schriftwahl mit zwei Spalten, 06.10.2026) — dann braucht es jeden davon.
 */
export function ladbareFonts(
  fontInfos: readonly FontInfoDto[],
): FontInfoDto[] {
  const gesehen = new Set<string>();
  return fontInfos.filter((fi) => {
    if (!fi?.familyName || !fi?.fontUrl) return false;
    const schnitt = `${fi.familyName}|${fi.weight || 400}|${fi.italic ? 1 : 0}`;
    if (gesehen.has(schnitt)) return false;
    gesehen.add(schnitt);
    return true;
  });
}

/**
 * Staerke eines Eintrags im fontMapping — NUR wenn sie ausdruecklich gewaehlt
 * wurde (Template-Editor/Branding, ab 06.10.2026). Aeltere Eintraege tragen
 * meist `weight: 400`, das nie jemand bewusst gesetzt hat; sie bleiben, wie
 * sie sind (Jans Entscheidung „Nur neu gewählte Schnitte“).
 */
export function gewaehlterSchnitt(
  eintrag: Partial<FontMappingEintrag> | undefined,
): number | undefined {
  const w = Number(eintrag?.weight);
  return eintrag?.schnittGewaehlt && Number.isFinite(w) && w > 0 ? w : undefined;
}

// Modul-weit: welche Fonts wurden bereits zum Laden ANGESTOSSEN (Familie+URL).
// Der document.fonts-Guard unten greift erst NACH Abschluss eines Loads. Bei
// langsamen Legacy-Fonts (ppi-backend, 2-5s) liess das ein weites Fenster offen,
// in dem die zwei Loader-Instanzen (Cover + Main in ScrollyViewer) plus
// Re-Renders dieselbe Familie mehrfach anstiessen → dieselbe Datei 4× geladen
// (WESER-KURIER Netzwerk-Waterfall 2026-08-13). Diese Menge deduped auch
// IN-FLIGHT + über Instanzen hinweg. Bewusst persistent über Navigationen:
// ein einmal geladener/versuchter Font muss nie erneut angestossen werden.
const bereitsAngestossen = new Set<string>();

export default function useCustomFontLoader(
  fontInfos: FontInfoDto[] | undefined,
  fontMapping: FontMapping | undefined,
): CustomFonts {
  const textFont = fontMapping?.text;
  const headlineFont = fontMapping?.headline;
  // Die Standard-Schriften (gemeinsames Register, auch unter ihren Google-
  // Namen) schon fuer das Cover: Der Renderer, der sie sonst registriert,
  // startet erst nach „Start". Idempotent.
  useEffect(() => {
    injectReaderStandardFonts();
  }, []);
  useEffect(() => {
    if (!fontInfos) return;
    for (const fi of ladbareFonts(fontInfos)) {
      const schluessel = fi.familyName + "|" + fi.fontUrl;
      if (bereitsAngestossen.has(schluessel)) {
        continue;
      }
      if (
        fontFaceSetIteratorToArray(document.fonts).some(
          (ff) => ff[0]?.family === fi.familyName,
        )
      ) {
        continue;
      }
      bereitsAngestossen.add(schluessel);
      const fontFace = new FontFace(fi.familyName, `url(${fi.fontUrl})`, {
        weight: String(fi.weight),
        style: fi.italic ? "italic" : "normal",
      });
      fontFace
        .load()
        .then((loadedFace) => {
          document.fonts.add(loadedFace);
        })
        // Veraltete Eintraege mit toter Adresse gab es schon immer (s. Kommentar
        // in ScrollyViewer). Frueher fielen sie nicht auf, weil sie ohnehin
        // uebersprungen wurden; jetzt werden sie geladen und duerfen still
        // scheitern — sonst haette der Scrolly wegen einer alten Schrift eine
        // unbehandelte Fehlermeldung.
        .catch(() => {});
    }
  }, [fontInfos, headlineFont, textFont]);

  const textWeight = gewaehlterSchnitt(textFont);
  const headlineWeight = gewaehlterSchnitt(headlineFont);
  return useMemo(() => {
    return {
      textFontFamily: textFont?.family,
      headlineFontFamily: headlineFont?.family,
      ...(textWeight ? { textFontWeight: textWeight } : {}),
      ...(headlineWeight ? { headlineFontWeight: headlineWeight } : {}),
    };
  }, [headlineFont?.family, textFont?.family, textWeight, headlineWeight]);
}
